import { useMarkerVisibility } from 'hooks/map/useMarkerVisibility';
import { useCreateWorldMarker } from 'hooks/marker/useCreateWorldMarker';
import { useCreateWorldMarkerV2 } from 'hooks/marker/v2/useCreateWorldMarkerV2';
import { isWithinRefs } from 'libs/utils/ref/refUtils';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { orderBy } from 'lodash';
import { MapMouseEvent } from 'mapbox-gl';
import { useContext } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { CtxDebug } from 'session/ctxDebug';
import { CtxEdit } from 'session/ctxEdit';

export const useWorldControls = (
    controlsRef: React.RefObject<HTMLDivElement[]>
): IReturnUseWorldControls => {
    const ctxEdit = useContext(CtxEdit);
    const ctxDebug = useContext(CtxDebug);
    const createWorldMarker = useCreateWorldMarker();
    const createWorldMarkerV2 = useCreateWorldMarkerV2();
    const markerVisibility = useMarkerVisibility();

    function selectMarker(markerId: string, extendSelection: boolean) {
        ctxEdit.setMarkerSidebar('properties');
        if (extendSelection) {
            ctxEdit.setSelectedMarkers([...ctxEdit.selectedMarkers, markerId]);
        } else {
            ctxEdit.setSelectedMarkers([markerId]);
        }
    }

    function getClickedMarker(e: MapMouseEvent) {
        if (e?.features?.length > 0) {
            const features = orderBy(e.features, (e) => e.properties?.orderIndex, 'desc');
            const feature = features[0];
            const source = feature.source.split('|')[0];
            const markers = ctxEdit.worldMarkers.filter(
                markerVisibility.isMarkerVisibleForSelectionInEdit
            );

            const marker = markers.find((marker) => marker.id === source);
            if (marker) {
                return marker;
            }

            if (feature.properties.markerId) {
                const marker = markers.find((marker) => marker.id === feature.properties.markerId);

                if (marker) {
                    return marker;
                }
            }
        }
    }

    async function onMapClick(e: MapMouseEvent) {
        if (isWithinRefs(controlsRef, e)) return;

        const map = ctxEdit.mapRef?.current?.getMap();
        if (!map) {
            console.warn('TODO: This should not be happening');
            return;
        }

        const lng = e.lngLat.lng;
        const lat = e.lngLat.lat;
        const zoom = map.getZoom() ?? 1;
        const point = map.project([lng, lat]);

        ctxDebug.setPoint(point);
        ctxDebug.setCoordinates({
            lat,
            lng
        });
        ctxDebug.setZoom(zoom);

        // console.log(
        //     e.features,
        //     'Lat:',
        //     lat.toFixed(2),
        //     ' Lng:',
        //     lng.toFixed(2),
        //     ' X:',
        //     point.x.toFixed(2),
        //     ' Y:',
        //     point.y.toFixed(2),
        //     ' Zoom:',
        //     zoom,
        //     lng,
        //     lat
        // );

        const isCtrlDown = e.originalEvent.ctrlKey || e.originalEvent.metaKey;

        if (ctxEdit.worldAction === EWorldAction.SELECT) {
            //This is not handled from the main useHook!
            const marker = getClickedMarker(e);
            if (marker) {
                selectMarker(marker.id, isCtrlDown);
            } else {
                selectMarker(null, isCtrlDown);
            }
        } else if (ctxEdit.worldAction === EWorldAction.TEXT) {
            createWorldMarker.createTextMarker(lat, lng, zoom);
        } else if (ctxEdit.worldAction === EWorldAction.IMAGE_V2) {
            createWorldMarker.createImageMarker(lat, lng, zoom);
        } else if (ctxEdit.worldAction === EWorldAction.REACT) {
            createWorldMarker.createReactMarker(lat, lng, zoom);
        } else if (ctxEdit.worldAction === EWorldAction.ICON_V2) {
            createWorldMarkerV2.createIcon(lat, lng);
        } else if (ctxEdit.worldAction === EWorldAction.VARIANT_ICON_V2) {
            createWorldMarkerV2.createVariantIcon(lat, lng);
        } else if (ctxEdit.worldAction === EWorldAction.TEXT_V2) {
            createWorldMarkerV2.createText(lat, lng);
        } else if (ctxEdit.worldAction === EWorldAction.INDOOR_STAND) {
            createWorldMarkerV2.createIndoorStand(lat, lng);
        }
    }

    return {
        onMapClick,
        getClickedMarker
    };
};

export interface IReturnUseWorldControls {
    onMapClick: (e: MapMouseEvent) => void;
    getClickedMarker: (e: MapMouseEvent) => IWorldMarker;
}
